// ---------------------------------------------------------------------------
// iconRegistry — string-name → LucideIcon mapping
// v0.16.10 — supports the WebMap-driven nav override flow.
//
// Lucide icons are React components and are NOT serializable to JSON. The
// nav override pipeline serializes nav items with the icon as a STRING name
// (e.g. "BookOpen") and resolves it back to the component at render time
// via this registry. Unknown names fall back to Circle to avoid throwing.
// ---------------------------------------------------------------------------

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BarChart3,
  BookOpen,
  BookText,
  Building2,
  Circle,
  ClipboardCheck,
  ClipboardList,
  Cpu,
  Factory,
  FileBarChart,
  FileCheck,
  FileText,
  FolderKanban,
  Globe,
  Hammer,
  Handshake,
  HardDrive,
  HeartPulse,
  History,
  LayoutDashboard,
  LibraryBig,
  Link,
  Link2,
  ListTree,
  Mail,
  Map,
  MapPin,
  Network,
  Package,
  Play,
  Presentation,
  Radio,
  ScrollText,
  Settings,
  Settings2,
  ShieldCheck,
  Tag,
  Truck,
  UserPlus,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

export const ICON_REGISTRY: Record<string, LucideIcon> = {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BarChart3,
  BookOpen,
  BookText,
  Building2,
  Circle,
  ClipboardCheck,
  ClipboardList,
  Cpu,
  Factory,
  FileBarChart,
  FileCheck,
  FileText,
  FolderKanban,
  Globe,
  Hammer,
  Handshake,
  HardDrive,
  HeartPulse,
  History,
  LayoutDashboard,
  LibraryBig,
  Link,
  Link2,
  ListTree,
  Mail,
  Map,
  MapPin,
  Network,
  Package,
  Play,
  Presentation,
  Radio,
  ScrollText,
  Settings,
  Settings2,
  ShieldCheck,
  Tag,
  Truck,
  UserPlus,
  Users,
  Wrench,
  Zap,
};

/** Sorted list of available icon names for pickers. */
export const ICON_NAMES: string[] = Object.keys(ICON_REGISTRY).sort();

/**
 * Resolve a string icon name to a LucideIcon component. Falls back to
 * Circle for unknown names so the sidebar never crashes on an unexpected
 * override payload.
 */
export function getIcon(name: string | undefined | null): LucideIcon {
  if (!name) return Circle;
  return ICON_REGISTRY[name] ?? Circle;
}

/**
 * Reverse lookup — given a LucideIcon component, return its string name.
 * Used during initial seeding (default nav → serialized override).
 */
export function getIconName(icon: LucideIcon): string {
  for (const [name, component] of Object.entries(ICON_REGISTRY)) {
    if (component === icon) return name;
  }
  return "Circle";
}
